import { subscribeToGlobalEvent } from "@storytell/events";
import { type StreamingChatId, StreamingChatStatus } from "@storytell/streaming";
import { type Accessor, createEffect } from "solid-js";
import { isServer } from "solid-js/web";
import { useWire } from "~/wire";
import { mergeRunningSources, type RunningChatEntry } from "./runningMerge";

// The submit event carries only the prompt text, not the chat id it becomes, so
// we hold the latest preview and claim it for whichever chat streams in the
// foreground next. That captures the label while the chat is on screen — before
// the user leaves and it surfaces as a background entry with no title yet.
const previewByChatId = new Map<StreamingChatId, string>();
let pendingPreview: string | undefined;
let captureStarted = false;

function startPreviewCapture(): void {
  if (captureStarted || isServer) return;
  captureStarted = true;
  subscribeToGlobalEvent("chat-prompt-bar-submitted", (e) => {
    pendingPreview = e.detail.promptPreview;
  });
}

export function getRunningChatPreview(chatId: StreamingChatId): string | undefined {
  return previewByChatId.get(chatId);
}

/**
 * Every prompt still generating for this user — the on-screen chat included, so
 * the panel's inventory never contradicts a visibly streaming thread. This
 * tab's live stream state unioned with the server-side registry snapshot, so a
 * fresh page load still sees turns launched before it.
 */
export function useRunningChats(): Accessor<RunningChatEntry[]> {
  const wire = useWire();
  startPreviewCapture();

  createEffect(() => {
    const foregroundId = wire.chat.getChatId();
    if (!foregroundId || pendingPreview === undefined || previewByChatId.has(foregroundId)) return;
    previewByChatId.set(foregroundId, pendingPreview);
    pendingPreview = undefined;
  });

  // Plain function, not createMemo: a memo read here intermittently yielded
  // undefined mid-transition on app open (ENG-6676), and memoizing buys
  // nothing — the result is a fresh array every run, so consumers re-run
  // regardless.
  const allGenerating = (): StreamingChatId[] => {
    if (isServer) return [];
    const chats = wire.stream.state.chats;
    const out: StreamingChatId[] = [];
    for (const id of Object.keys(chats) as StreamingChatId[]) {
      if (!chats[id]) continue;
      if (wire.stream.chat.getStatus(id) !== StreamingChatStatus.GENERATING) continue;
      out.push(id);
    }
    return out;
  };

  createEffect(() => {
    const active = new Set(allGenerating());
    // Server-known chats keep their previews too: a completion can race the
    // snapshot refetch and the label shouldn't flicker to the generic one.
    for (const entry of wire.notifications.serverRunning()) {
      active.add(entry.chatId as StreamingChatId);
    }
    for (const id of previewByChatId.keys()) {
      if (!active.has(id)) previewByChatId.delete(id);
    }
  });

  // Plain function for the same reason as allGenerating: as a createMemo this
  // accessor also read as undefined mid-transition on app open, crashing
  // consumers on running().length (ENG-6676).
  return () => {
    if (isServer) return [];
    return mergeRunningSources(allGenerating(), wire.notifications.serverRunning());
  };
}
